import React from "react";
import styled from "styled-components";
import { Alert, Card, Col, Empty, Progress, Row, Space, Table, Tag, Typography } from "antd";
import { StarFilled } from "@ant-design/icons";

const { Text, Title } = Typography;

const CommandSection = styled.section`
  display: grid;
  gap: 16px;
`;

const KpiCard = styled(Card)`
  border-radius: 14px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  height: 100%;

  .ant-card-body {
    padding: 18px 20px;
  }
`;

const KpiLabel = styled(Text)`
  display: block;
  color: #5f6b6a;
  font-size: 13px;
  margin-bottom: 6px;
`;

const KpiValue = styled(Title)`
  && {
    margin: 0;
    color: #005249;
    font-size: 28px;
    line-height: 1.15;
  }
`;

const KpiMeta = styled(Text)`
  display: block;
  margin-top: 8px;
  color: #8aa8a3;
  font-size: 12px;
`;

const HeroBanner = styled(Card)`
  border-radius: 14px;
  border: 1px solid #c5ddd9;
  background: #f4faf8;

  .ant-card-body {
    padding: 16px 20px;
  }
`;

const PanelCard = styled(Card)`
  border-radius: 14px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  height: 100%;

  .ant-card-head-title {
    color: #005249;
    font-weight: 700;
  }
`;

const FunnelRow = styled.div`
  display: grid;
  grid-template-columns: 140px 1fr auto;
  gap: 10px;
  align-items: center;
  margin-bottom: 10px;
`;

const ProductRow = styled.div`
  display: grid;
  grid-template-columns: 110px 1fr auto;
  gap: 10px;
  align-items: center;
  margin-bottom: 10px;
`;

const RiskCard = styled(Alert)`
  border-radius: 12px;
`;

const FinanceCommandCentre = ({ commandCentre }) => {
  if (!commandCentre) {
    return (
      <Alert
        type="info"
        showIcon
        message="Command Centre unavailable"
        description="Settled contribution metadata has not been attached to finance metrics yet."
      />
    );
  }

  const kpis = commandCentre.kpis || {};
  const contribution = kpis.settledContributionPerJob || {};
  const breakEven = commandCentre.breakEven || {};
  const pressure = kpis.cashPressure || {};
  const unit = commandCentre.unitEconomics || {};
  const products = commandCentre.contributionByProduct || [];
  const funnel = commandCentre.funnel?.stages || [];
  const risk = commandCentre.biggestRisk || {};

  return (
    <CommandSection data-testid="finance-command-centre">
      <Row gutter={[16, 16]}>
        <Col xs={24} md={12} xl={6}>
          <KpiCard data-testid="command-kpi-cash">
            <KpiLabel>Cash available</KpiLabel>
            <KpiValue level={3}>{formatMoneyOrDash(kpis.cashAvailable?.value)}</KpiValue>
            <KpiMeta>
              Runway {formatMonthsOrDash(kpis.cashRunwayMonths?.value)}
            </KpiMeta>
            <ConfidenceTag value={kpis.cashAvailable?.dataConfidence} />
          </KpiCard>
        </Col>
        <Col xs={24} md={12} xl={6}>
          <KpiCard data-testid="command-kpi-contribution">
            <Space size={8} wrap>
              <KpiLabel>Settled contribution / job</KpiLabel>
              <Tag color="green">SETTLED</Tag>
            </Space>
            <KpiValue level={3}>
              {formatMoneyOrDash(contribution.value)}
            </KpiValue>
            <KpiMeta>
              {contribution.settledJobCount || 0} settled of{" "}
              {contribution.completedJobCount ?? "—"} completed
            </KpiMeta>
            <ConfidenceTag value={contribution.dataConfidence} />
          </KpiCard>
        </Col>
        <Col xs={24} md={12} xl={6}>
          <KpiCard data-testid="command-kpi-breakeven">
            <KpiLabel>Jobs vs break-even</KpiLabel>
            <KpiValue level={3}>
              {formatJobsVsBreakEven(kpis.jobsVsBreakEven)}
            </KpiValue>
            <KpiMeta>
              {kpis.jobsVsBreakEven?.gap == null
                ? "Need fixed overhead + settled contribution"
                : `${kpis.jobsVsBreakEven.gap} more settled jobs to break even`}
            </KpiMeta>
            <ConfidenceTag value={kpis.jobsVsBreakEven?.dataConfidence} />
          </KpiCard>
        </Col>
        <Col xs={24} md={12} xl={6}>
          <KpiCard data-testid="command-kpi-pressure">
            <KpiLabel>Cash conversion pressure</KpiLabel>
            <KpiValue level={3}>{formatMoneyOrDash(pressure.value)}</KpiValue>
            <KpiMeta>
              Receivables {formatMoneyOrDash(pressure.receivables)} · Pending
              payouts {formatMoneyOrDash(pressure.pendingPayouts)}
            </KpiMeta>
            <ConfidenceTag value={pressure.dataConfidence} />
          </KpiCard>
        </Col>
      </Row>

      <HeroBanner data-testid="command-hero">
        <Space align="start" size={12}>
          <StarFilled style={{ color: "#005249", marginTop: 4 }} />
          <Text strong>{commandCentre.hero?.message}</Text>
        </Space>
      </HeroBanner>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={8}>
          <PanelCard title="Financial funnel">
            {funnel.length ? (
              funnel.map((stage) => (
                <FunnelRow key={stage.key}>
                  <Text>{formatStageLabel(stage.key)}</Text>
                  <Progress
                    percent={funnelPercent(stage, funnel)}
                    showInfo={false}
                    strokeColor="#005249"
                  />
                  <Text strong>{formatStageValue(stage)}</Text>
                </FunnelRow>
              ))
            ) : (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No funnel data" />
            )}
          </PanelCard>
        </Col>
        <Col xs={24} lg={8}>
          <PanelCard title="Contribution by product">
            <Text type="secondary">Settled contribution per job</Text>
            {products.length ? (
              products.map((row) => (
                <ProductRow key={row.product}>
                  <Text>{formatProductLabel(row.product)}</Text>
                  <Progress
                    percent={row.sharePercent || 0}
                    showInfo={false}
                    strokeColor="#005249"
                  />
                  <Text strong>{formatMoneyOrDash(row.contributionPerJob)}</Text>
                </ProductRow>
              ))
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No settled product mix yet"
              />
            )}
          </PanelCard>
        </Col>
        <Col xs={24} lg={8}>
          <PanelCard title="Break-even">
            <Progress
              type="dashboard"
              percent={Math.min(100, Number(breakEven.progressPercent || 0))}
              strokeColor="#005249"
              format={() =>
                breakEven.requiredJobs == null
                  ? "—"
                  : `${breakEven.actualSettledJobs || 0}/${breakEven.requiredJobs}`
              }
            />
            <KpiMeta>
              Fixed overhead {formatMoneyOrDash(breakEven.fixedOverhead)} · Uses
              settled contribution, not allocated leftover
            </KpiMeta>
            <ConfidenceTag value={breakEven.dataConfidence} />
          </PanelCard>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={10}>
          <PanelCard title="Unit economics (settled)">
            <Table
              size="small"
              pagination={false}
              rowKey="key"
              dataSource={unitEconomicsRows(unit)}
              columns={[
                { title: "Metric", dataIndex: "label", key: "label" },
                {
                  title: "Per job",
                  dataIndex: "value",
                  key: "value",
                  render: formatMoneyOrDash,
                },
              ]}
            />
            <KpiMeta>
              North star is settled contribution before allocated overhead.
            </KpiMeta>
          </PanelCard>
        </Col>
        <Col xs={24} lg={7}>
          <PanelCard title="Cash position">
            {commandCentre.cashPosition?.dataConfidence === "actual" ? (
              <Table
                size="small"
                pagination={false}
                rowKey="key"
                dataSource={cashPositionRows(commandCentre.cashPosition)}
                columns={[
                  { title: "Flow", dataIndex: "label", key: "label" },
                  {
                    title: "Amount",
                    dataIndex: "value",
                    key: "value",
                    render: formatMoneyOrDash,
                  },
                ]}
              />
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="Upload an FNB Gold Business PDF on Finance → Reconciliation to light this up."
              />
            )}
            <ConfidenceTag value={commandCentre.cashPosition?.dataConfidence} />
          </PanelCard>
        </Col>
        <Col xs={24} lg={7}>
          <RiskCard
            data-testid="command-biggest-risk"
            type={riskAlertType(risk.severity)}
            showIcon
            message={risk.title || "Financial risk"}
            description={risk.detail}
          />
        </Col>
      </Row>
    </CommandSection>
  );
};

function cashPositionRows(position = {}) {
  return [
    { key: "opening", label: "Opening", value: position.openingCash },
    { key: "received", label: "Customer receipts", value: position.cashReceived },
    { key: "payouts", label: "Provider payouts", value: position.providerPayouts },
    { key: "opex", label: "Operating expenses", value: position.operatingExpenses },
    { key: "closing", label: "Closing cash", value: position.closingCash },
  ];
}

function unitEconomicsRows(unit = {}) {
  return [
    { key: "revenue", label: "Customer cash received", value: unit.customerRevenuePerJob },
    { key: "payout", label: "Provider payout (actual)", value: unit.providerPaidPerJob },
    { key: "variable", label: "Variable job costs", value: unit.variableCostsPerJob },
    {
      key: "settled",
      label: "Settled contribution",
      value: unit.settledContributionPerJob,
    },
    {
      key: "overhead",
      label: "Allocated overhead (management)",
      value: unit.allocatedOverheadPerJob,
    },
    {
      key: "after",
      label: "After allocated overhead",
      value: unit.contributionAfterOverheadPerJob,
    },
  ];
}

function ConfidenceTag({ value }) {
  const tone =
    value === "actual"
      ? "green"
      : value === "derived_from_actuals"
        ? "blue"
        : value === "estimated"
          ? "gold"
          : "default";
  return <Tag color={tone}>{formatConfidence(value)}</Tag>;
}

function formatConfidence(value) {
  if (value === "actual") return "Actual";
  if (value === "derived_from_actuals") return "Derived from actuals";
  if (value === "estimated") return "Estimated";
  return "Insufficient data";
}

function formatMoneyOrDash(value) {
  if (value == null || Number.isNaN(Number(value))) return "—";
  return new Intl.NumberFormat("en-ZA", {
    style: "currency",
    currency: "ZAR",
    maximumFractionDigits: 0,
  }).format(Number(value));
}

function formatMonthsOrDash(value) {
  if (value == null) return "—";
  return `${Number(value).toFixed(1)} months`;
}

function formatJobsVsBreakEven(jobsVsBreakEven = {}) {
  if (jobsVsBreakEven.required == null) {
    return `${jobsVsBreakEven.actual ?? 0} / —`;
  }
  return `${jobsVsBreakEven.actual ?? 0} / ${jobsVsBreakEven.required}`;
}

function formatStageLabel(key) {
  const labels = {
    leads: "Leads",
    quotes: "Quotes",
    bookings: "Bookings",
    completed_jobs: "Completed",
    customer_cash: "Customer cash",
    settled_contribution: "Settled contribution",
  };
  return labels[key] || key;
}

function formatStageValue(stage) {
  if (stage.value != null) return formatMoneyOrDash(stage.value);
  if (stage.count != null) return stage.count;
  return "—";
}

function funnelPercent(stage, stages) {
  const counts = stages
    .map((row) => Number(row.count))
    .filter((value) => Number.isFinite(value) && value > 0);
  const max = Math.max(...counts, 1);
  if (stage.count != null) return Math.round((Number(stage.count) / max) * 100);
  return stage.value != null ? 100 : 0;
}

function formatProductLabel(product) {
  if (product === "share_load" || product === "shared_load") return "Shared load";
  if (product === "exclusive") return "Exclusive";
  if (product === "local") return "Local";
  if (product === "corridor") return "Corridor";
  return product;
}

function riskAlertType(severity) {
  if (severity === "intervention") return "error";
  if (severity === "watch") return "warning";
  return "success";
}

export default FinanceCommandCentre;
